import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseISO, format } from 'date-fns';

import { getBannerEndDate, isEndlessBannerDate } from './utils';
import { IBigBannerSlide } from './models';
import { BigBannerSlideButton } from '../_SlideButton/BigBannerSlideButton';
import { Unit } from '../../../core/Unit';
import { BreakpointsContext } from '../../../providers/Breakpoints';
import { MOBILE_COEFF } from './consts';
import { Box } from '@src/atoms/Box';
import { Text } from '@src/atoms/Text';
import { Avatar } from '@src/atoms/Avatar';
import { Grid } from '@src/atoms/Grid';
import { Slider } from '@src/atoms/Slider';
import { classnames } from '@src/utils/classnames';
import { TChangeFeedbackData } from '../models';

import styled from './BigBannerSlide.scss';

interface IProps extends IBigBannerSlide {
  changeFeedbackData: TChangeFeedbackData;
}

export const BigBannerSlide: FC<IProps> = (props) => {
  const { id, title, name, logo, description, expirationAt, image, link, linkText, changeFeedbackData, entityId } =
    props;
  const { isMobile } = useContext(BreakpointsContext);
  const [sliderHeight, setSliderHeight] = useState(0);
  const slideRef = useRef<HTMLDivElement | null>(null);

  const classes = classnames(styled.root, styled.root, {
    [styled.mobile]: isMobile,
  });

  const img = useMemo(() => ({ url: image, alt: name }), [image, name]);

  useEffect(() => {
    if (slideRef.current) {
      const height = slideRef.current.getBoundingClientRect().height;
      setSliderHeight(height);
    }
  }, []);

  return (
    <Unit id={'BigBannerSlide'} entityCtx={{ entityId: entityId ? entityId : 'banner', objectId: `${id}` }}>
      <Box key={id} className={classes} data-slide-id={id}>
        <Box
          className={styled.description}
          column
          alignItems="start"
          justifyContent="spaceBetween"
          paddingTop={{ DESKTOP: 32 }}
          paddingRight={{ DESKTOP: 32, MOBILE: 20 }}
          paddingBottom={{ DESKTOP: 32, MOBILE: 24 }}
          paddingLeft={{ DESKTOP: 32, MOBILE: 20 }}
          padding={{ MOBILE: 16 }}
        >
          <div>
            <Text id={'title'} size="h5" uppercase color="secondaryYellow">
              {title}
            </Text>
            <Box marginTop={{ DESKTOP: 16, MOBILE: 12 }} marginBottom={{ DESKTOP: 12, MOBILE: 8 }}>
              {logo && (
                <Box marginRight={{ DESKTOP: 12, MOBILE: 8 }}>
                  <Avatar className={styled.avatar} firstName={name} imageUrl={logo} withBg={false} />
                </Box>
              )}
              <Box alignItems="center">
                <Text id={'name'} size="h4">
                  {name}
                </Text>
              </Box>
            </Box>
            <Box marginTop={{ DESKTOP: 12, MOBILE: 8 }} marginBottom={16}>
              <Text id="description" size="secondary">
                <div dangerouslySetInnerHTML={{ __html: description }} />
              </Text>
            </Box>
          </div>
          <Grid columns={2} className={styled.buttonAndExpirationDate}>
            <Box>
              <BigBannerSlideButton link={link} linkText={linkText} changeFeedbackData={changeFeedbackData} />
            </Box>
            {expirationAt && !isEndlessBannerDate(expirationAt) && (
              <Box marginLeft={8} alignItems="center" justifyContent="end">
                <Text id="expirationAt" size="caption" align="right">
                  {format(parseISO(expirationAt), getBannerEndDate(expirationAt))}
                </Text>
              </Box>
            )}
          </Grid>
        </Box>
        <div className={styled.rightContent} ref={slideRef}>
          <Slider.Item img={img} height={sliderHeight} coeff={isMobile ? MOBILE_COEFF : undefined} />
        </div>
      </Box>
    </Unit>
  );
};
